import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isTenantAdminUser, useAuthStore } from '@store/authStore';
import { useCheckAuth } from '@api/hooks/useCheckAuth';
import { useErpAuth } from '@api/hooks/useQuickBooksAuth';
import { useGetErpConnection } from '@api/hooks/useErpConnection';
import { useOnboardingConfig } from '@api/hooks/useOnboarding';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import { LoginForm } from '@components/Auth/LoginForm';
import { ToastContainer } from '@components/common/Toast';
import { useNotificationStore } from '@store/notificationStore';
import { getSsoLoginErrorMessage } from '@utils/ssoErrorMessages';
import { getErpProviderLabel, isErpAuthProvider } from '@api/quickbooksAuth';

/**
 * Auth Loading Page
 *
 * Handles the authentication flow:
 * 1. If not logged in: Show login form
 * 2. If logged in but QB not authenticated: Check QB status and redirect if needed
 * 3. If fully authenticated: Redirect to dashboard
 */
export const AuthLoadingPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const redirectAfterLogin = searchParams.get('redirect');
  const ssoError = searchParams.get('error');
  const { isAuthenticated, currentUser } = useAuthStore();
  const tenantAdmin = isTenantAdminUser(currentUser);
  const { addNotification } = useNotificationStore();

  // Surface SSO login failures (see GET /api/v1/sso/microsoft/login/callback,
  // which redirects here with `?error=<code>` on failure) through the same
  // toast mechanism the password-login flow uses (see useLogin's onError).
  useEffect(() => {
    if (!ssoError) return;

    const message = getSsoLoginErrorMessage(ssoError);
    if (message) {
      addNotification({
        message,
        type: ssoError === 'sso_cancelled' ? 'info' : 'error',
      });
    }

    // Drop `error` from the URL so refreshing/navigating doesn't re-show it.
    const next = new URLSearchParams(searchParams);
    next.delete('error');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoError]);

  // Step 1: Check backend auth status (restores session from localStorage and validates it)
  const { isLoading: isCheckingAuth, isError: authCheckError } = useCheckAuth();

  // Fetch vendor field configuration (called once at app init)
  const { isLoading: isLoadingVendorFields } = useOnboardingConfig(
    Boolean(currentUser?.onboarding_complete)
  );

  // Fetch ERP connection to check if ERP integration is configured
  const { data: erpConnection, isLoading: isLoadingErp } = useGetErpConnection();
  const hasErpIntegration = Boolean(erpConnection?.realm_id);

  // Step 2: Check ERP auth status ONLY after user is authenticated AND ERP integration is configured
  const provider = isErpAuthProvider(erpConnection?.erp_type)
    ? erpConnection.erp_type
    : null;
  const providerLabel = getErpProviderLabel(erpConnection?.erp_type);
  const { authState, checkAndInitializeAuth } = useErpAuth({
    enabled: isAuthenticated && !isCheckingAuth && tenantAdmin && hasErpIntegration && Boolean(provider),
    provider: provider ?? 'quickbooks',
  });

  // Trigger ERP auth check when authentication state changes (only if ERP is configured)
  useEffect(() => {
    if (isAuthenticated && !isCheckingAuth && tenantAdmin && hasErpIntegration && provider) {
      console.log(`[AuthLoadingPage] Triggering ${providerLabel} auth check...`);
      checkAndInitializeAuth();
    }
  }, [isAuthenticated, isCheckingAuth, tenantAdmin, hasErpIntegration, checkAndInitializeAuth, providerLabel]);

  // Effect to handle navigation after authentication is complete
  useEffect(() => {
    // If auth check is done and user is not authenticated, show login form
    if (!isCheckingAuth && !isAuthenticated) {
      return; // Stay on login form
    }

    // If auth check is done and user IS authenticated
    if (!isCheckingAuth && isAuthenticated && !isLoadingErp && !isLoadingVendorFields) {
      if (!hasErpIntegration) {
        // No ERP integration configured - skip QB auth and go to dashboard (or redirect target)
        const destination = redirectAfterLogin ?? (currentUser?.onboarding_complete === false ? '/onboarding' : '/');
        navigate(destination);
        return;
      }

      if (!tenantAdmin) {
        const destination = redirectAfterLogin ?? (currentUser?.onboarding_complete === false ? '/onboarding' : '/');
        navigate(destination);
        return;
      }

      if (!provider) {
        const destination = redirectAfterLogin ?? (currentUser?.onboarding_complete === false ? '/onboarding' : '/');
        navigate(destination);
      } else if (!authState.isChecking) {
        if (authState.isAuthenticated) {
          // Fully authenticated with ERP - redirect to dashboard or onboarding (or redirect target)
          const destination = redirectAfterLogin ?? (currentUser?.onboarding_complete === false ? '/onboarding' : '/');
          navigate(destination);
        } else if (authState.authorizationUrl) {
          // Not ERP authenticated - redirect to ERP OAuth login
          window.location.href = authState.authorizationUrl;
        }
      }
    }
  }, [isCheckingAuth, isAuthenticated, isLoadingErp, isLoadingVendorFields, hasErpIntegration, tenantAdmin, provider, authState.isChecking, authState.isAuthenticated, authState.authorizationUrl, navigate, currentUser, redirectAfterLogin]);

  const authPage = (children: React.ReactNode) => (
    <div className="min-h-screen flex items-center justify-center bg-[#060e1f] p-4">
      {/* Subtle teal glow — matches public site */}
      <div className="pointer-events-none fixed inset-0 flex items-center justify-center">
        <div className="h-[600px] w-[600px] rounded-full bg-teal-500/5 blur-3xl" />
      </div>
      <div className="relative z-10">{children}</div>
      {/* ToastContainer normally lives in Layout, which isn't mounted on this
          unauthenticated route — mount it here so login/SSO error and
          success notifications are actually visible. */}
      <ToastContainer />
    </div>
  );

  // Step 1: Still verifying session from backend
  if (isCheckingAuth) {
    return authPage(<LoadingSpinner text="Verifying session…" />);
  }

  // Step 1 Failed: No valid session - show login form
  if (!isAuthenticated || authCheckError) {
    return authPage(
      <LoginForm
        onSuccess={() => {
          window.location.href = redirectAfterLogin
            ? `/auth?redirect=${encodeURIComponent(redirectAfterLogin)}`
            : '/auth';
        }}
      />
    );
  }

  // Loading ERP settings to determine if integration is configured
  if (isLoadingErp) {
    return authPage(<LoadingSpinner text="Loading settings…" />);
  }

  // Loading vendor field configuration
  if (isLoadingVendorFields) {
    return authPage(<LoadingSpinner text="Loading configuration…" />);
  }

  // Step 2: Checking ERP authentication (only when ERP is configured)
  if (tenantAdmin && hasErpIntegration && provider && authState.isChecking) {
    return authPage(<LoadingSpinner text={`Initializing ${providerLabel}…`} />);
  }

  // Fallback (shouldn't reach here)
  return authPage(<LoadingSpinner />);
};
